import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type MfaFactor = {
  id: string;
  status: 'verified' | 'unverified';
  factor_type: 'totp';
  friendly_name?: string;
};

export type AalLevel = 'aal1' | 'aal2';

export type MfaStatus = {
  factors: MfaFactor[];
  verifiedFactor: MfaFactor | null;
  currentLevel: AalLevel;
  nextLevel: AalLevel;
  isEnrolled: boolean;
  isVerifiedThisSession: boolean;
  isLoading: boolean;
  refresh: () => Promise<void>;
};

export function useMfaStatus(): MfaStatus {
  const [factors, setFactors] = useState<MfaFactor[]>([]);
  const [currentLevel, setCurrentLevel] = useState<AalLevel>('aal1');
  const [nextLevel, setNextLevel] = useState<AalLevel>('aal1');
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const [factorsResult, aalResult] = await Promise.all([
        supabase.auth.mfa.listFactors(),
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      ]);
      setFactors((factorsResult.data?.totp ?? []) as MfaFactor[]);
      if (aalResult.data) {
        setCurrentLevel(aalResult.data.currentLevel as AalLevel);
        setNextLevel(aalResult.data.nextLevel as AalLevel);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const verifiedFactor = factors.find(f => f.status === 'verified') ?? null;

  return {
    factors,
    verifiedFactor,
    currentLevel,
    nextLevel,
    isEnrolled: factors.some(f => f.status === 'verified'),
    isVerifiedThisSession: currentLevel === 'aal2',
    isLoading,
    refresh,
  };
}
